import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment as env } from '@env/environment';
import { Observable, throwError } from 'rxjs';
import { CountryCode } from '@app/shared/utils/enums/country-code.enum';

@Injectable({ providedIn: 'root' })
export class ApiService {
  readonly baseUrl = {
    bff: env.bffApiUrl,
    product: env.productApiUrl,
    srsProduct: env.srsProductApiUrl,
    clinic: env.clinicApiUrl,
    tool: env.toolApiUrl,
    nutrition: env.nutritionApiUrl,
    healthNutrition: env.healthNutritionApiUrl,
    petOwners: env.petOwnersApiUrl,
    core: env.coreApiUrl,
    ciam: env.ciam.apiUrl,
    blob: env.blobUrl,
    blobApi: env.blobApiUrl,
    myAccountUrl: env.myAccountUrl,
    domain: env.ciam.redirectCallback,
    sendBackData: env.sendBackDataApiUrl,
  };

  readonly pathUrl = {
    // BFF
    bff_petOwner: (clinicId: string, petOwnerId: string) => `${this.baseUrl.bff}/api/petOwner/${clinicId}/${petOwnerId}`,

    // Products
    productsV1: (clinicId: string): string => `${this.baseUrl.clinic}/Clinics/${clinicId}/clinicProductPrices`,
    productsV2: (clinicId: string): string => `${this.baseUrl.clinic}/Clinics/${clinicId}/V2/products`,
    productsSrs: (): string => `${this.baseUrl.srsProduct}/products`,
    productDetailV2: (clinicId: string, productId: string): string => `${this.baseUrl.clinic}/Clinics/${clinicId}/V2/products/${productId}`,
    productDetailSrsCore: (id: string) => `${this.baseUrl.core}/products/${id}`,
    productDetailSrs: (id: string) => `${this.baseUrl.srsProduct}/products/${id}`,

    // Contact
    contacts: (clinicId: string) => `${this.baseUrl.clinic}/Clinics/${clinicId}/contacts/`,
    contactById: (clinicId: string, contactId: string) => `${this.baseUrl.clinic}/Clinics/${clinicId}/contacts/${contactId}`,
    contactsSearch: (clinicId: string) => `${this.baseUrl.clinic}/Clinics/${clinicId}/contacts/searches`,

    // smart reco
    smartRecoProducts: (clinicId) => `${this.baseUrl.clinic}/Clinics/${clinicId}/v2-new/products/product-recommendations`,
    nutritionalAssessment: () => `${this.baseUrl.clinic}/nutritional-xray`,
    smartRecoNotRecommendedProduct: (clinicId: string, productId: string) =>
      `${this.baseUrl.clinic}/Clinics/${clinicId}/v2-new/products/product-recommendations/${productId}`,
    srsSmartRecoNotrecommendedProduct: (productId: string) => `${this.baseUrl.srsProduct}/products/v2/recommendations/${productId}`,
    smartRecoProductsSrs: () => `${this.baseUrl.srsProduct}/products/v2/recommendations`,
    smartRecoSendBackData: () => `${this.baseUrl.sendBackData}/send-recommendation`,

    // health nutrition
    recommendationPrograms: `${this.baseUrl.healthNutrition}/Recommendations/programs`,
    ibw: `${this.baseUrl.healthNutrition}/digital/diagnostics/ibw`,
    ibwCheck: `${this.baseUrl.healthNutrition}/digital/diagnostics/ibw/check`,
    digitalRationing: `${this.baseUrl.healthNutrition}/digital/rationing/ration`,
    planNextVisit: `${this.baseUrl.healthNutrition}/visits/nextVisits`,

    // Vet
    vet: (vetId) => `${this.baseUrl.clinic}/Vets/${vetId}`,
    clinic: (clinicId) => `${this.baseUrl.clinic}/Clinics/${clinicId}`,
    patient: (clinicId, patientId) => `${this.baseUrl.clinic}/Clinics/${clinicId}/patients/${patientId}`,
    patients: (clinicId) => `${this.baseUrl.clinic}/Clinics/${clinicId}/patients`,
    pet: (clinicId, petId) => `${this.baseUrl.clinic}/Clinics/${clinicId}/pets/${petId}`,
    consultations: (clinicId: string, patientId: string) =>
      `${this.baseUrl.clinic}/Clinics/${clinicId}/patients/${patientId}/consultations`,
    consultation: (clinicId: string, patientId: string, consultationId: string) =>
      `${this.baseUrl.clinic}/Clinics/${clinicId}/patients/${patientId}/consultations/${consultationId}`,
    updateConsultationVisits: (clinicId: string, patientId: string, consultationId: string) =>
      `${this.baseUrl.clinic}/Clinics/${clinicId}/patients/${patientId}/consultations/${consultationId}/visits`,
    invitations: (clinicId: string) => `${this.baseUrl.clinic}/Clinics/${clinicId}/invitations`,
    search: (clinicId: string) => `${this.baseUrl.clinic}/Clinics/${clinicId}/patients/search`,
    petProfile: (breedCode: string): string => `${this.baseUrl.clinic}/metadata/breeds/${breedCode}/profiles`,
    vetsCiamDetails: (clinicId: string) => `${this.baseUrl.clinic}/Clinics/${clinicId}/vetsCiamDetails`,
    createPatient: (clinicId: string) => `${this.baseUrl.clinic}/Clinics/${clinicId}/patient`,
    createConsultation: (clinicId: string, patientId: string) =>
      `${this.baseUrl.clinic}/Clinics/${clinicId}/patients/${patientId}/consultations`,

    // growth chart
    growthChartStandardWeight: (petId: string) => `${this.baseUrl.clinic}/v2/pets/${petId}/weights/walthamCharts`,

    // CIAM
    vetUpdate: (vetId) => `${this.baseUrl.ciam}/directory/users/${vetId}`,

    // Onboarding
    userVet: () => `${this.baseUrl.clinic}/vets`,
    lookupClinic: (customerId: string, countryCode: string) =>
      `${this.baseUrl.clinic}/Clinics?customerId=${customerId}&country=${countryCode}`,
    connectVetClinic: (clinicId: string) => `${this.baseUrl.clinic}/Clinics/${clinicId}/vets`,

    // T&Cs
    terms: (vetId: string) => `${this.baseUrl.clinic}/Vets/${vetId}/termsconditions`,
    purpose: (countryCode: CountryCode): string => `${this.baseUrl.clinic}/purposes?country=${countryCode}`,

    // Vet Mails
    contactUs: `${this.baseUrl.clinic}/Contacts/mail/contact-us`,
    mailRecommendation: `${this.baseUrl.clinic}/Contacts/mail/recommendation`,
    signalRecommendation: `${this.baseUrl.clinic}/Contacts/mail/ununderstood-recommendation`,
    uploadPictures: (clinicId: string) => `${this.baseUrl.clinic}/clinics/${clinicId}/pictures`,

    // Renal-detect
    renalDetectRecommendation: `${this.baseUrl.healthNutrition}/predictions`,

    // Blob storage
    blobStorageData: (countryCode: string) => `${this.baseUrl.blob}/data_${countryCode}.json?${env.blobQueryParams}`,
    recoConsentData: `${this.baseUrl.blob}/reco_consent/reco_consent.json?${env.blobQueryParams}`,
    featureTogglesData: `${this.baseUrl.blob}/FE_Feature_Toggling/FE_Feature_Toggling.json?${env.blobQueryParams}`,

    // Breeds
    breeds: `${this.baseUrl.clinic}/metadata/breeds`,

    myAccount: {
      fromProfile: `${this.baseUrl.myAccountUrl}/manager?redirect_uri=${this.baseUrl.domain}/account`,
      editPersonalInformation: `${this.baseUrl.myAccountUrl}/manager/personal-infos?redirect_uri=${this.baseUrl.domain}/account`,
      editContactInformation: `${this.baseUrl.myAccountUrl}/manager/personal-infos#contact?redirect_uri=${this.baseUrl.domain}/account`,
      editSecurity: `${this.baseUrl.myAccountUrl}/manager/security?redirect_uri=${this.baseUrl.domain}/account`,
    },

    // PO letter
    poLetter: (templateName: string) => `${this.baseUrl.clinic}/clinics/po-letter/${templateName}/save-pdf`,
    poLetterPreview: (templateName: string) => `${this.baseUrl.clinic}/clinics/po-letter/${templateName}/pdf`,

    // Vet Source
    sendPimsConsultationEvent: (clinicId: string, pimsName: 'vetSources') =>
      `${this.baseUrl.clinic}/Clinics/${clinicId}/sendPimsConsultationEvent?pimsName=${pimsName}`,

    // Pets
    petsWeight: (clinicId: string, petId: string) => `${this.baseUrl.clinic}/Clinics/${clinicId}/pets/${petId}/weights`,
    petsDraft: (clinicId: string) => `${this.baseUrl.clinic}/Clinics/${clinicId}/pets/draft`,

    // Analytics
    sendShareRecoEvent: (clinicId: string) => `${this.baseUrl.clinic}/Clinics/${clinicId}/sendSharedRecoEvent`,
  };

  params(parameters: any): HttpParams {
    let params = new HttpParams();
    Object.keys(parameters).forEach(function (key) {
      params = params.append(key, parameters[key]);
    });
    return params;
  }

  handleError(httpError: HttpErrorResponse): Observable<string> {
    if (httpError?.error?.errorCode === 'GROWTH_PROGRAM_TARGET_WEIGHT_NOT_APPLICABLE') {
      return throwError(() => new Error('DAC_adult-target-weight-outside-range_no-ration-provided'));
    }

    if (httpError.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', httpError.error.message);
    } else if (httpError.error && httpError.error.error) {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      console.error(httpError.error, `Backend returned code ${httpError.status}, ` + `body was: ${httpError.error.error}`);

      return throwError(() => new Error(httpError.error.error.message));
    }
    // return an observable with a user-facing error message
    return throwError(() => new Error($localize`:@@error_try-again-later:`));
  }
}
